
import {
  ColumnDef,
  CoreOptions,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslations } from '@/hooks/useTranslations'
import { ThHTMLAttributes, useEffect, useMemo, useState } from 'react'

import { Button, Checkbox, Skeleton } from '@/components/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@/constants/datatable'
import CharacterEmptyIcon from '~icons/dl/character-empty.jsx'
import SortAscIcon from '~icons/dl/sort-asc.jsx'
import SortDescIcon from '~icons/dl/sort-desc.jsx'
import SortNoneIcon from '~icons/dl/sort-none.jsx'

import { PageSizeSwitcher } from './PageSizeSwitcher'
import { Pagination } from './Pagination'
import { cn } from '@/lib/utils'
import type { CSSProperties } from 'react'

/** Sticky column offsets + opaque fill (raw var(--background) is invalid — tokens are HSL components). */
function getStickyCellStyle<TData, TValue>(
  items: { column: { columnDef: ColumnDef<TData, TValue>; getSize: () => number } }[],
  index: number,
): CSSProperties | undefined {
  const columnDef = items[index]?.column.columnDef as ColumnDef<TData, TValue> & {
    sticky?: 'left' | 'right'
  }
  const sticky = columnDef.sticky
  if (!sticky) return undefined

  const base: CSSProperties = {
    position: 'sticky',
    zIndex: sticky === 'left' ? 2 : 3,
    backgroundColor: 'hsl(var(--card))',
  }

  if (sticky === 'left') {
    let left = 0
    for (let i = 0; i < index; i++) {
      const def = items[i]?.column.columnDef as ColumnDef<TData, TValue> & { sticky?: 'left' | 'right' }
      if (def.sticky === 'left') {
        left += items[i].column.getSize()
      }
    }
    return {
      ...base,
      left,
      borderRight: '1px solid hsl(var(--border))',
    }
  }

  let right = 0
  for (let i = index + 1; i < items.length; i++) {
    const def = items[i]?.column.columnDef as ColumnDef<TData, TValue> & { sticky?: 'left' | 'right' }
    if (def.sticky === 'right') {
      right += items[i].column.getSize()
    }
  }
  return {
    ...base,
    right,
    borderLeft: '1px solid hsl(var(--border))',
  }
}

interface DataTableProps<TData, TValue> {
  columns: (ColumnDef<TData, TValue> & { sticky?: 'left' | 'right' })[]
  data: TData[]
  sorting?: SortingState
  setSorting?: (sorting: SortingState) => void
  pagination?: PaginationState
  setPagination?: (pagination: PaginationState) => void
  pageSizeOptions?: number[]
  totalPages?: number
  showPageSize?: boolean
  showPagination?: boolean
  isLoading?: boolean
  selectable?: boolean
  isSelectable?: (_row: Row<TData>) => boolean
  rowSelection?: RowSelectionState
  setRowSelection?: (_rowSelection: Updater<RowSelectionState>) => void
  getRowId?: CoreOptions<TData>['getRowId']
  manualPagination?: boolean
  manualSorting?: boolean
  totalReport?: React.ReactNode
}

export function RenderHeader<TData, TValue>({ column, getContext }: Header<TData, TValue>) {
  const isSortable = column.columnDef.enableSorting
  const sortDirection = column.getIsSorted()

  if (isSortable) {
    return (
      <Button
        type="button"
        variant="link"
        className="p-0 hover:no-underline space-x-1"
        onClick={() => column.toggleSorting(undefined, true)}
      >
        <span className="font-bold text-t-secondary">{flexRender(column.columnDef.header, getContext())}</span>
        {sortDirection === 'asc' ? (
          <SortAscIcon className="size-4 text-t-secondary" />
        ) : sortDirection === 'desc' ? (
          <SortDescIcon className="size-4 text-t-secondary" />
        ) : (
          <SortNoneIcon className="size-4 text-t-secondary " />
        )}
      </Button>
    )
  }

  return <span>{flexRender(column.columnDef.header, getContext())}</span>
}

export function DataTable<TData, TValue>({
  columns,
  data,
  sorting,
  setSorting,
  pagination,
  setPagination,
  totalPages,
  showPageSize = false,
  showPagination = false,
  isLoading = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  isFilter = false,
  enableMultiSort = false,
  selectable = false,
  isSelectable,
  rowSelection,
  setRowSelection,
  getRowId,
  manualPagination = true,
  manualSorting = true,
  getDeletedRow,
  totalReport,
}: DataTableProps<TData, TValue> & {
  isFilter?: boolean
  enableMultiSort?: boolean
  getDeletedRow?: (_row: Row<TData>) => boolean
}) {
  const t = useTranslations()

  const formattedColumns = useMemo(() => {
    const formatted = [] as (ColumnDef<TData, TValue> & { sticky?: 'left' | 'right' })[]

    if (selectable) {
      formatted.push({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
            className="translate-y-[2px] bg-s-primary border-b-primary data-[state=checked]:border-0 disabled:bg-s-secondary disabled:border-b-disabled"
          />
        ),
        cell: ({ row }) => {
          const selectable = isSelectable?.(row) ?? true

          return (
            <div className="text-center">
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                disabled={!selectable}
                aria-label="Select row"
                className="translate-y-[2px] bg-s-primary border-b-primary data-[state=checked]:border-0 disabled:bg-s-secondary disabled:border-b-disabled"
              />
            </div>
          )
        },
        enableSorting: false,
        enableHiding: false,
        size: 32,
        sticky: 'left',
        meta: {
          align: 'center',
        },
      })
    }

    formatted.push(...columns)

    return formatted
  }, [columns, selectable, t])

  const table = useReactTable({
    data,
    columns: formattedColumns,
    getCoreRowModel: getCoreRowModel(),
    state: { sorting, pagination, rowSelection },
    manualSorting,
    enableMultiSort: enableMultiSort,
    onSortingChange: (updater) => {
      if (!setSorting || !sorting) return

      const newSortingValue = typeof updater === 'function' ? updater(sorting) : updater

      setSorting?.(newSortingValue)
    },
    manualPagination,
    onPaginationChange: (updater) => {
      if (!setPagination || !pagination) return

      const newPaginationValue = typeof updater === 'function' ? updater(pagination) : updater

      if (newPaginationValue.pageSize !== pagination.pageSize) {
        newPaginationValue.pageIndex = 0
      }

      setPagination?.(newPaginationValue)
    },
    enableRowSelection: isSelectable ?? true,
    onRowSelectionChange: (updater) => {
      if (!setRowSelection || !rowSelection) return

      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater

      setRowSelection?.(newSelection)
    },
    getRowId,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="relative w-full">
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                data-role="header"
              >
                {headerGroup.headers.map((header, headerIndex) => {
                  const stickyStyle = getStickyCellStyle<TData, TValue>(headerGroup.headers, headerIndex)
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth: header.getSize(),
                        ...stickyStyle,
                      }}
                      align={
                        (header.column.columnDef.meta as Record<string, string>)?.align as ThHTMLAttributes<'th'>['align']
                      }
                    >
                      {header.isPlaceholder ? null : <RenderHeader {...header} />}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <TableRow
                key={`loading-${index}`}
                data-state="loading"
              >
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton className="h-6 rounded" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={cn(getDeletedRow?.(row) && 'line-through')}
              >
                {row.getVisibleCells().map((cell, cellIndex) => {
                  const stickyStyle = getStickyCellStyle<TData, TValue>(row.getVisibleCells(), cellIndex)
                  return (
                    <TableCell
                      key={cell.id}
                      style={{
                        ...stickyStyle,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          ) : (
            <TableRow data-role="no-result">
              <TableCell
                colSpan={columns.length}
                className="p-8 text-center hover:bg-s-primary"
              >
                <span className="text-sm font-semibold text-t-secondary">
                  {isFilter ? t('common.components.datatable.no_results') : t('common.components.datatable.empty')}
                </span>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </Table>
      </div>
      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center flex-wrap gap-2" data-id="page-size-switcher">
          {showPageSize && (
            <PageSizeSwitcher
              options={pageSizeOptions}
              initialPageSize={table.getState().pagination.pageSize}
              onPageSizeChange={(pageSize) => {
                table.setPageSize(pageSize)
              }}
            />
          )}
          {totalReport && (
            totalReport
          )}
        </div>
        <div data-id="pagination" className="flex justify-center lg:justify-end">
          {showPagination && (
            <Pagination
              currentPage={table.getState().pagination.pageIndex + 1}
              totalPages={totalPages || 0}
              onPageChange={(pageIndex) => {
                table.setPageIndex(pageIndex - 1)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
